import type {
  AnalyticsChartQueryParams,
  AnalyticsChartResponse,
} from './types';

import {
  keepPreviousData,
  QueryFunctionContext,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';

import { queryKeysAnalytics } from '../queryKeys';

import { fetchAnalyticsChart } from './fetchAnalyticsChart';
import { mapChartData } from './mapChartData';
import { zodAnalyticsChartQueryParamsSchema } from './validations';

/**
 * Generates the query options for the analytics chart query so they can be shared between the useQuery hook and the prefetching.
 *
 * @param queryParams - The query parameters for fetching the analytics chart.
 * @param options - Additional options for the query.
 * @param ignoreSignal - If true, the abort signal is not passed to the fetch function.
 *   Use this for prefetching in route loaders to prevent request cancellation when the loader completes.
 * @returns The query options including queryKey, queryFn, and other settings.
 */
export const getAnalyticsChartQueryOptions = ({
  queryParams,
  options,
  ignoreSignal = false,
}: {
  queryParams: AnalyticsChartQueryParams;
  options?: Omit<
    UseQueryOptions<AnalyticsChartResponse, Error, AnalyticsChartResponse>,
    'queryKey' | 'queryFn'
  >;
  ignoreSignal?: boolean;
}) => {
  const validatedQueryParams =
    zodAnalyticsChartQueryParamsSchema.parse(queryParams);

  const isEnabled = Boolean(
    validatedQueryParams.name &&
    validatedQueryParams.dateRange.dateFrom &&
    validatedQueryParams.dateRange.dateTo,
  );

  return {
    enabled: isEnabled,
    queryFn: async ({ signal }: QueryFunctionContext) =>
      fetchAnalyticsChart(validatedQueryParams, {
        signal: ignoreSignal ? undefined : signal,
      }),
    queryKey: queryKeysAnalytics.getChart(validatedQueryParams),
    select: mapChartData,
    staleTime: ms('5m'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the analytics chart data with given query parameters.
 *
 * @param queryParams - The query parameters for fetching the analytics chart.
 * @param options - Additional options for the useQuery hook.
 * @returns The result of the useQuery hook.
 * @see https://app.dev.vestigas.com/redoc#tag/User-Activity/operation/get_weekly_active_users_chart_user_activity_chart_weekly_active_users_post
 */
export const useQueryAnalyticsChart = (
  queryParams: AnalyticsChartQueryParams,
  options?: Omit<
    UseQueryOptions<AnalyticsChartResponse, Error, AnalyticsChartResponse>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    ...getAnalyticsChartQueryOptions({
      options,
      queryParams,
    }),
    placeholderData: keepPreviousData,
  });
